import { inject, InjectionToken } from '@angular/core';
import { Params } from '@angular/router';
import { Observable } from 'rxjs';

import { APP_CONFIG } from '../../core/config/app-config.model';
import { FilterOptionsService } from '../../core/filters/filter-options.service';
import { AccessTokenProvider } from '../../core/realtime/access-token.provider';
import { canonicalizeClientCode } from '../../core/session/client-code-canonicalizer';
import type { BreadcrumbLabelSource } from '../../shell/breadcrumb/breadcrumb-label.contract';

/**
 * The breadcrumb label for `/clients/:clientCode` (AITH-46 U3), replacing AITH-42's stub that
 * resolved the literal `'Client'`.
 *
 * **It emits twice, and the first emission is synchronous.** `BreadcrumbService` displays the
 * route's static label until a source resolves, and this route's static label is `'Client'` — so an
 * asynchronous-only source paints that literal on every navigation before replacing it. The client
 * code is already in the route parameters and needs no lookup, so it goes out immediately, inside
 * the subscribe; `BreadcrumbService.upgradeLabel` captures its `navigationId` before subscribing,
 * which is what makes a synchronous emission land rather than be discarded as stale.
 *
 * The second emission is the display name, from whichever source can supply it:
 *
 * 1. **The directory cache**, when the filter bar's client selector has already populated it. Free
 *    and synchronous.
 * 2. **`GET /clients/{code}/summary`**, otherwise — which is what a deep link into a cold session
 *    hits. Chosen over the directory for the fallback because it answers for **both** caller
 *    categories, where `GET /clients` is staff-only: today only the staff route carries this source,
 *    but a client-scoped route that later adopts it must not silently get a code where staff get a
 *    name.
 *
 * Anything that fails leaves the code standing. That is a real identifier, so the breadcrumb is
 * never blank and never the literal `'Client'` — the two failures rule 4 and rule 7 of the
 * breadcrumb contract call out.
 */
class ClientBreadcrumbLabelSource implements BreadcrumbLabelSource {
  private readonly options = inject(FilterOptionsService);
  private readonly config = inject(APP_CONFIG);
  private readonly accessTokenProvider = inject(AccessTokenProvider);

  resolveLabel(params: Params): Observable<string> {
    const raw = typeof params['clientCode'] === 'string' ? params['clientCode'] : '';
    const code = canonicalizeClientCode(raw) ?? raw.trim();

    return new Observable<string>((subscriber) => {
      if (code === '') {
        // Structurally unreachable — the route requires the segment. Emitting nothing leaves the
        // static label rather than publishing an empty one, which `applyIfFresh` would drop anyway.
        subscriber.complete();
        return undefined;
      }

      subscriber.next(code);

      const cached = this.options.clientDisplayName(code);
      if (cached !== code) {
        subscriber.next(cached);
        subscriber.complete();
        return undefined;
      }

      const controller = new AbortController();
      void this.resolveName(code, controller.signal).then((name) => {
        if (name !== null) subscriber.next(name);
        subscriber.complete();
      });
      // `BreadcrumbService` unsubscribes on the next navigation, which aborts the request with it.
      return () => controller.abort();
    });
  }

  /**
   * The generated client is loaded through a DYNAMIC import on purpose.
   *
   * This module is imported by `app.routes.ts`, which is in the eager bootstrap graph, so a static
   * import here would pull `ClientReferenceApi` and the runtime into the initial bundle for the
   * benefit of one breadcrumb on one route. `import()` keeps it in a lazy chunk, and the chunk is
   * one the detail page loads anyway.
   */
  private async resolveName(clientCode: string, signal: AbortSignal): Promise<string | null> {
    try {
      const [{ ClientReferenceApi }, { Configuration }] = await Promise.all([
        import('../../api/generated/apis/ClientReferenceApi'),
        import('../../api/generated/runtime'),
      ]);

      const summary = await new ClientReferenceApi(
        new Configuration({
          basePath: this.config.apiBaseUrl.replace(/\/$/, ''),
          accessToken: () => this.accessTokenProvider.getAccessToken(signal),
        }),
      ).getClientSummary({ clientCode }, { signal });

      const name = summary.name?.trim();
      return name ? name : null;
    } catch {
      // Permanent fallback to the code, with no console output (breadcrumb contract rule 7). A 403
      // here is ordinary: the guard admits the route, the API decides the tenant.
      return null;
    }
  }
}

export const CLIENT_BREADCRUMB_LABEL = new InjectionToken<BreadcrumbLabelSource>(
  'CLIENT_BREADCRUMB_LABEL',
  { providedIn: 'root', factory: () => new ClientBreadcrumbLabelSource() },
);
